import { endPointCheckLogin, endPointlogin, endPointLogout, endPointSubscriptionStatus } from "modules/main/services/endpoints";
import { ReactNode, FC, useState, useEffect, useCallback, useRef } from "react";
import { createContext } from "react";
import {useLocation, useNavigate } from "react-router-dom";
import { AuthError, loginFormType} from "../types/form-login-types";
import { UserData } from "../types/user-data";
import { base64ToUtf8 } from "utils/encoding";
import apiClient from "modules/main/services/apiClient";
import { usePostHog } from "posthog-js/react";
import { useNativeAuthListener } from "../hooks/useNativeAuthListener";
import { LoginRegisterDialog } from "../components/loginDialog";
import { getVisitorId } from "utils/analytics";
import { toast } from "sonner";
import { MainLogo } from "modules/main/assets/main_logo";
import { hasSelectedLocation, readUserDataFromLocalStorage } from "modules/main/services/location-storage";

const CONSOLE_DEBUG  = false;
const USER_DATA_STORAGE_KEY = "userData";
const NEW_USER_EFFECT_WINDOW_MS = 2500;

interface LoginResponse {
    userData?: string;
}

interface SubscriptionStatusData {
    premium_user: boolean;
    plan_type: string | null;
    premium_expires_at: string | null;
}

interface AuthContextType {
    loginAuth: (values: loginFormType, from:string) => Promise<void>;
    loginAuthGoogle:(response:{credential:string, redirect_uri:string}) => Promise<void>;
    checkAuth:(userData?:string) => Promise<boolean>,
    logout: () => Promise<void>,
    isLogged:boolean,
    isNewUser:boolean,
    isPremiumUser:boolean,
    markNewUserHandled: () => void,
    requireLoginAction: () => boolean,
    refreshSubscriptionStatus: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
    loginAuth: async () => {},
    loginAuthGoogle: async () => {},
    checkAuth:async () => false,
    logout: async () => {},
    isLogged:false,
    isNewUser:false,
    isPremiumUser:false,
    markNewUserHandled: () => {},
    requireLoginAction: () => false,
    refreshSubscriptionStatus: async () => {}

});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const navigate = useNavigate();    
    const location = useLocation();
    const pathName = location.pathname;
    const [isLogged, setLogged] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false); // Para controlar la inicialización
    const [loginDialogOpen, setLoginDialogOpen] = useState<boolean>(false);
    const [isNewUser, setIsNewUser] = useState<boolean>(false);
    const [isPremiumUser, setIsPremiumUser] = useState<boolean>(false);
    const authStateVersionRef = useRef(0);

    const syncPremiumStateFromStorage = useCallback(() => {
        try {
            const rawUserData = localStorage.getItem(USER_DATA_STORAGE_KEY);
            if (!rawUserData) {
                setIsPremiumUser(false);
                return;
            }

            const parsedUserData = JSON.parse(rawUserData) as UserData;
            setIsPremiumUser(Boolean(parsedUserData.premium_user));
        } catch (error) {
            console.error("Error parsing premium state from localStorage userData:", error);
            setIsPremiumUser(false);
        }
    }, [isLogged]);

    /**
     * Updates the persisted local user profile in localStorage.
     */
    const updateStoredUserData = useCallback((updater: (current: UserData) => UserData): void => {
        try {
            const rawUserData = localStorage.getItem(USER_DATA_STORAGE_KEY);
            if (!rawUserData) {
                return;
            }

            const parsedUserData = JSON.parse(rawUserData) as UserData;
            const updatedUserData = updater(parsedUserData);
            localStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(updatedUserData));
        } catch (error) {
            console.error("Error updating localStorage userData:", error);
        }
    }, []);

    /**
     * Marks new-user onboarding as handled and avoids welcome retriggering.
     */
    const markNewUserHandled = useCallback(() => {
        setIsNewUser(false);
        updateStoredUserData((currentUserData) => ({
            ...currentUserData,
            new_user: false
        }));
    }, [updateStoredUserData]);

    const posthog = usePostHog();

    /**
     * Identifies the authenticated user in PostHog with the shared profile payload.
     */
    const identifyAuthenticatedUser = useCallback((userData: UserData): void => {
        posthog.identify(userData.id.toString(), {
            email: userData.username,
            name: `${userData.first_name} ${userData.last_name}`.trim(),
            registered_user: true
        });
    }, [posthog]);

    // Log de cambios en el estado de autenticación
    useEffect(() => {
        if (CONSOLE_DEBUG)
            console.log("Auth state changed - isLogged:", isLogged, "isInitialized:", isInitialized);
    }, [isLogged, isInitialized]);
    //Listen native auth injection
    useNativeAuthListener(
    {
        onSessionReceived: (userData) => {
            authStateVersionRef.current += 1;
            localStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(userData));
            
            // 2. Tracking
            identifyAuthenticatedUser(userData);

            // 3. Actualizar estado React
            setLogged(true);
            setIsInitialized(true);
            setIsNewUser(Boolean(userData.new_user));
            setIsPremiumUser(Boolean(userData.premium_user) && isLogged);
            apiClient.resetLogoutState();
            if (CONSOLE_DEBUG)
                console.log("Auth Provider: Sesión nativa aplicada correctamente.");
            }
    }
    );


    // Función helper para verificar si hay datos válidos en localStorage
    const hasValidLocalData = (): boolean => {
        try {
            const userData = localStorage.getItem(USER_DATA_STORAGE_KEY);
            if (!userData) return false;
            
            const parsed = JSON.parse(userData);
            return parsed && (parsed.user_id || parsed.id || parsed.email);
        } catch (error) {
            console.error("Error parsing localStorage userData:", error);
            return false;
        }
    };


    useEffect(() => {
        // Solo hacer el check inicial si no se ha inicializado aún
        if (!isInitialized) {
            // console.log("Initializing auth state...");
            
            // Verificar si hay datos válidos en localStorage como indicador rápido
            if (hasValidLocalData()) {
                console.log("Found valid userData in localStorage, checking server...");
                // Si hay datos válidos, asumir autenticado temporalmente mientras verificamos
                setLogged(true);
                syncPremiumStateFromStorage();
            }
            
            const initialAuthStateVersion = authStateVersionRef.current;
            const initializeAuth = async () => {
                try {
                    const response = await checkAuthFetch(false);
                    if (CONSOLE_DEBUG)
                        console.log("Initial auth check result:", response);

                    if (authStateVersionRef.current !== initialAuthStateVersion) {
                        setIsInitialized(true);
                        return;
                    }

                    setLogged(response);
                    if (response) {
                        syncPremiumStateFromStorage();
                        apiClient.resetLogoutState();
                    } else {
                        setIsPremiumUser(false);
                    }
                    setIsInitialized(true);
                } catch (error) {
                    console.error("Error during initial auth check:", error);

                    if (authStateVersionRef.current === initialAuthStateVersion) {
                        setLogged(false);
                    }
                    setIsInitialized(true);
                }
            };

            void initializeAuth();
        }

        // Escuchar el evento de logout global
        const handleGlobalLogout = () => {
            if (CONSOLE_DEBUG)
                console.log("Global logout event received");
            authStateVersionRef.current += 1;
            setLogged(false);
            setIsNewUser(false);
            setIsPremiumUser(false);
            localStorage.removeItem(USER_DATA_STORAGE_KEY);
            if (location.pathname !== "/") {
                navigate("/");
            }
        };

        window.addEventListener("auth:logout", handleGlobalLogout);

        return () => {
            window.removeEventListener("auth:logout", handleGlobalLogout);
        };
    }, [isInitialized, location.pathname, navigate, syncPremiumStateFromStorage]);
    
    const checkAuthFetch = async (allowStateUpdates = true): Promise<boolean> => {
        try{
            // Usar fetch nativo para evitar el ciclo de refresh automático del apiClient
            const response = await fetch(endPointCheckLogin, {
                method: "GET",
                credentials: "include"
            });
            
            if (CONSOLE_DEBUG)
                console.log("Auth check HTTP status:", response.status);
            
            if (!response.ok) {
                if (CONSOLE_DEBUG)
                    console.log("Auth check failed with status:", response.status);
                if (hasValidLocalData()) {
                    if (allowStateUpdates) {
                        setLogged(true);
                        syncPremiumStateFromStorage();
                        apiClient.resetLogoutState();
                    }
                    return true;
                }
                if (allowStateUpdates) {
                    setLogged(false);
                }
                return false;
            }
            
            const data = await response.json();

            if (CONSOLE_DEBUG)
                console.log("Auth check response data:", data);
            
            // Si la respuesta incluye un refresh automático, hacer una segunda verificación
            if (data.retry_request === true) {
                // console.log("Token was refreshed, making second auth check...");
                
                // Esperar un poco y hacer una segunda verificación
                await new Promise(resolve => setTimeout(resolve, 100));
                
                try {
                    const secondResponse = await fetch(endPointCheckLogin, {
                        method: "GET",
                        credentials: "include"
                    });
                    
                    if (secondResponse.ok) {
                        const secondData = await secondResponse.json();
                        console.log("Second auth check response:", secondData);
                        
                        const isAuthenticated = secondData && (
                            secondData.authenticated === true ||
                            secondData.user_id ||
                            secondData.userData ||
                            secondData.user ||
                            secondData.status === "authenticated" ||
                            secondData.status === true
                        );
                        
                        if (isAuthenticated) {
                            if (allowStateUpdates) {
                                setLogged(true);
                                syncPremiumStateFromStorage();
                                apiClient.resetLogoutState();
                            }
                            return true;
                        }
                    }
                } catch (secondError) {
                    // console.error("Second auth check failed:", secondError);
                }
                
                // Si llegamos aquí, asumir que está autenticado porque el refresh fue exitoso
                // console.log("Assuming authenticated due to successful token refresh");
                if (allowStateUpdates) {
                    setLogged(true);
                    syncPremiumStateFromStorage();
                    apiClient.resetLogoutState();
                }
                return true;
            }
            
            // Verificar específicamente si hay indicadores de usuario autenticado
            const isAuthenticated = data && (
                data.authenticated === true ||
                data.user_id ||
                data.userData ||
                data.user ||
                data.status === "authenticated" ||
                data.status === true
            );
            
            if (isAuthenticated) {
                if (CONSOLE_DEBUG)
                    console.log("User is authenticated");
                if (allowStateUpdates) {
                    setLogged(true);
                    syncPremiumStateFromStorage();
                    apiClient.resetLogoutState();
                }
                return true;
            } else {
                // Verificar localStorage como último recurso antes de limpiar
                if (hasValidLocalData()) {
                    // console.log("Found valid userData in localStorage, maintaining authenticated state");
                    if (allowStateUpdates) {
                        setLogged(true);
                        syncPremiumStateFromStorage();
                        apiClient.resetLogoutState();
                    }
                    return true;
                } else {
                    // Solo marcar como no autenticado si:
                    // 1. No hay datos válidos en localStorage
                    // 2. No estamos en proceso de refresh
                    // 3. La respuesta del servidor indica claramente que no hay sesión
                    // console.log("No authentication found");
                    if (allowStateUpdates) {
                        setLogged(false);
                        setIsPremiumUser(false);
                    }
                    
                    // Ser muy conservativo con limpiar localStorage durante refresh
                    if (allowStateUpdates && !apiClient.isTokenRefreshing && !hasValidLocalData()) {
                        // console.log("Clearing localStorage - no valid session found");
                        localStorage.removeItem(USER_DATA_STORAGE_KEY);
                    } else if (apiClient.isTokenRefreshing) {
                        // console.log("Token refresh in progress - preserving localStorage");
                    }
                    return false;
                }
            }
        }catch(error){            
            console.error("Auth check failed with error:", error);
            // No limpiar localStorage en caso de errores de red/temporales
            // Solo marcar como no logueado temporalmente
            if (allowStateUpdates) {
                setLogged(false);
                setIsPremiumUser(false);
            }
            return false;
        }        
    }

    /**
     * Persists backend user data, synchronizes auth flags and identifies the user in analytics.
     */
    const createUserData = useCallback((userDataString: string) => {
        const decodedJson= base64ToUtf8(userDataString);
        const parsedUserData = JSON.parse(decodedJson) as UserData;
        const shouldShowWelcome = parsedUserData.new_user === true;

        identifyAuthenticatedUser(parsedUserData);

        if (shouldShowWelcome) {
            posthog.capture("new_user_detected");
        }

        setIsNewUser(shouldShowWelcome);
        setIsPremiumUser(Boolean(parsedUserData.premium_user));

        try {
            localStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(parsedUserData));
        } catch (storageError) {
            console.warn("No se pudo guardar userData en localStorage:", storageError);
            // La app puede continuar funcionando sin localStorage
        }

    }, [identifyAuthenticatedUser, posthog]);


    const checkAuth = useCallback(async (userData?:string): Promise<boolean> => {            
            if (isLogged){
                return true;
            } 

            if(userData){
                authStateVersionRef.current += 1;
                createUserData(userData);
                setLogged(true);
                // Resetear el estado de logout en el apiClient
                apiClient.resetLogoutState();
                return true;
            }             
            return await checkAuthFetch();
        }, [createUserData, isLogged]);

    const loginAuthGoogle = useCallback(async (response:{credential:string, redirect_uri:string}) => {
        try{
            // console.log("Starting Google authentication request to backend");
            const currentUserData = readUserDataFromLocalStorage();
            const shouldSendLocation = hasSelectedLocation(currentUserData);
            const res = await apiClient.post<LoginResponse>(endPointlogin, {
                code: response.credential,
                redirect_uri: response.redirect_uri,
                ...(shouldSendLocation ? { location_id: currentUserData.location_id } : {})
            },{
                headers: {
                    "content-type":"application/json",
                    "x-visitor-id": getVisitorId()
                }
            });

            const userDataMatch = res; // Already parsed JSON from apiClient
            // console.log("Google authentication response received from backend");

            if(userDataMatch && typeof userDataMatch.userData === "string" && userDataMatch.userData.length > 0){
                authStateVersionRef.current += 1;
                createUserData(userDataMatch.userData);
                setLogged(true);
                // Resetear el estado de logout en el apiClient
                apiClient.resetLogoutState();
                return;
            }

            // Backend responded without usable userData — fail loudly so callers
            // don't navigate into protected routes with no auth state.
            throw new AuthError("Google login response missing userData");

        }catch(error){
            console.error("Google authentication error:", error);
            if (error instanceof Error) {
                throw new AuthError(error.message);
            }
            throw error;
        }
    }, [createUserData]);

    const loginAuth = useCallback(async (values:loginFormType, from:string) => {    
        try{
            console.log("Starting authentication request to backend for user:", values.username);
            const userDataMatch = await apiClient.post<LoginResponse>(endPointlogin, values, {
                headers: {
                    "content-type":"application/json",
                    "x-visitor-id": getVisitorId()
                }
            });

            if(userDataMatch.userData){
                authStateVersionRef.current += 1;
                createUserData(userDataMatch.userData);
                setLogged(true);
                // Resetear el estado de logout en el apiClient
                apiClient.resetLogoutState();
                
                navigate(from);                
            }            
        }catch(error){   
            console.error("Error during loginAuth:", error);         
            if (error instanceof Error) {
                throw new AuthError(error.message);
            }
            throw error;
        }
    }, [createUserData, navigate]);


    

    useEffect(() => {
        if (!isNewUser) {
            return;
        }
        // toast.custom(() => (
        //     <div className="w-full text-xs rounded-lg border bg-popover px-4 py-3 text-popover-foreground shadow-lg">
        //         <div className="flex flex-col gap-2">
        //             <div className="flex flex-row items-end gap-2">
        //                 <MainLogo classname="h-5 w-5" />
        //                 <span className="font-extrabold">Bienvenido a Carriapp</span>
        //             </div>
        //             <p className="text-xs text-muted-foreground">
        //                 Tu cuenta ha sido creada. <span className="!font-extrabold text-popover-foreground">Suscribete y prueba por 2 semanas Carriapp Plus gratis</span>.
        //             </p>
        //         </div>
        //     </div>
        // ));

        const timeoutId = window.setTimeout(() => {
            markNewUserHandled();
        }, NEW_USER_EFFECT_WINDOW_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [isNewUser, markNewUserHandled]);

    const logout = useCallback(async () => {
        // console.log("Logout function called, current isLogged:", isLogged);
        try {
            // Primero limpiar el estado local para evitar que apiClient intente refresh
            authStateVersionRef.current += 1;
            setLogged(false);
            posthog.reset();
            setIsNewUser(false);
            setIsPremiumUser(false);
            localStorage.removeItem(USER_DATA_STORAGE_KEY);
            // console.log("Local state cleared, isLogged set to false");
            
            // Hacer el logout en el servidor
            await apiClient.post(endPointLogout);
            // console.log("Server logout successful");
        } catch (error) {
            console.error("Error during logout:", error);
            // Incluso si falla el logout en el servidor, limpiar localmente
            setLogged(false);
            setIsNewUser(false);
            setIsPremiumUser(false);
            localStorage.removeItem(USER_DATA_STORAGE_KEY);
        } finally {
            // Disparar evento de logout global para que otros componentes se actualicen
            // console.log("Dispatching auth:logout event");
            window.dispatchEvent(new CustomEvent("auth:logout"));
            
            // Solo navegar si no estamos ya en la home
            if (location.pathname !== "/") {
                // console.log("Navigating to home");
                navigate("/");
            }
        }
    }, [isLogged, location.pathname, navigate]);

    const requireLoginAction =(() => {
        // console.log("Action requires login - checking login status");
        if(isLogged){
            return true;
        }
        // console.log("Action requires login - opening login dialog");
        setLoginDialogOpen(true);
        return false;
    });

    /**
     * Refreshes the subscription status from the backend and updates local state and localStorage.
     */
    const refreshSubscriptionStatus = useCallback(async () => {
        if (!isLogged) {
            return;
        }

        try {
            const response = await fetch(endPointSubscriptionStatus, {
                method: "GET",
                credentials: "include",
            });

            if (response.ok) {
                const data: SubscriptionStatusData = await response.json();

                setIsPremiumUser(data.premium_user);

                // Update localStorage userData with new premium status
                updateStoredUserData((current) => ({
                    ...current,
                    premium_user: data.premium_user,
                }));
            }
        } catch (error) {
            console.error("Error refreshing subscription status:", error);
        }
    }, [isLogged, updateStoredUserData]);

    return(
        <AuthContext.Provider value={{loginAuth,loginAuthGoogle, checkAuth,logout,isLogged, isNewUser, isPremiumUser, markNewUserHandled, requireLoginAction, refreshSubscriptionStatus}}>
            {children}
            {loginDialogOpen && (
                <LoginRegisterDialog
                    headLess={true} setOpen={setLoginDialogOpen} open={loginDialogOpen}
                    loginRegisterSwitch="register"
                />
            )}
        </AuthContext.Provider>
    )
}
